import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAuth } from 'react-oidc-context';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

const createApolloClient = (accessToken, graphQLURI) => {
  const httpLink = createUploadLink({
    uri: graphQLURI
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = accessToken;

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : ''
      }
    };
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            tiles: {
              keyArgs: args =>
                args.listingId
                  ? ['listingId', args.listingId, 'layerType', args.layerType]
                  : ['layerType', args.layerType],
              merge(
                existing = {
                  items: [],
                  hasNext: true,
                  page: 0,
                  limit: 100,
                  totalCount: 0
                },
                incoming,
                { readField }
              ) {
                const mergedItems = existing ? existing.items.slice(0) : [];
                if (incoming.items) {
                  incoming.items.forEach(newItem => {
                    if (
                      !mergedItems.some(
                        existingItem =>
                          readField('id', existingItem) ===
                          readField('id', newItem)
                      )
                    ) {
                      mergedItems.push(newItem);
                    }
                  });
                }
                return {
                  items: mergedItems,
                  hasNext: incoming.hasNext,
                  page: incoming.page,
                  limit: incoming.limit,
                  totalCount: incoming.totalCount
                };
              }
            }
          }
        }
      }
    })
  });
};

const useApollo = () => {
  const auth = useAuth();
  const { graphQLURI } = useSelector(state => state.config);
  const client = useMemo(
    () => createApolloClient(auth.user?.access_token, graphQLURI),
    [auth]
  );

  return client;
};

export default useApollo;
